import React from 'react';
import { motion } from 'framer-motion';
import Footer from '../../../components/Footer';
import {
    GraduationCap,
    Stars,
    Clock,
    Bookmark,
    ArrowUpRight,
    Users,
    BookOpen,
    Award,
    Globe,
    Rocket,
    FlaskConical,
    Microscope,
    Atom,
    Leaf
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ScienceImg from "../../../assets/images/science.jpeg";
import Title from '../../../components/TitleComponent';

const bscCourses = [
    { name: "B.SC CBZ/B.SC CBZ HONS", path: "bsc/chemistry-botany-zoology" },
    { name: "B.Sc. (PCM) / Hons. B.Sc – Hons.", path: "bsc/physics-chemistry-maths" },
    { name: "B.Sc. Environmental Sciences / Hons.", path: "bsc/environmental-science" }
];

const mscCourses = [
    { name: "M.Sc. in Physics", path: "msc/physics" },
    { name: "M.Sc. in Chemistry", path: "msc/chemistry" },
    { name: "M.Sc. in Mathematics", path: "msc/mathematics" },
    { name: "M.Sc. in Zoology", path: "msc/zoology" },
    { name: "M.Sc. in Botany", path: "msc/botany" },
    { name: "M.Sc. in Environmental Sciences", path: "msc/environmental-science" }
];

const Stats = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-4 md:my-8 px-4"
    >
        {[
            { icon: <Users className="w-6 h-6 md:w-8 md:h-8" />, number: "1500+", label: "Students Enrolled", color: "bg-blue-50" },
            { icon: <BookOpen className="w-6 h-6 md:w-8 md:h-8" />, number: "9+", label: "Programs Offered", color: "bg-green-50" },
            { icon: <Award className="w-6 h-6 md:w-8 md:h-8" />, number: "90%", label: "Research Success Rate", color: "bg-purple-50" },
            { icon: <Globe className="w-6 h-6 md:w-8 md:h-8" />, number: "40+", label: "Research Projects", color: "bg-orange-50" }
        ].map((stat, index) => (
            <motion.div
                key={index}
                className={`${stat.color} p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300`}
                whileHover={{ scale: 1.02 }}
            >
                <div className="text-[#0e4d38]">{stat.icon}</div>
                <h3 className="text-xl md:text-2xl font-bold mt-2 text-gray-800">{stat.number}</h3>
                <p className="text-gray-600 text-xs md:text-sm">{stat.label}</p>
            </motion.div>
        ))}
    </motion.div>
);

const CourseCard = ({ course, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="group w-full"
    >
        <Link to={course.path}>
            <div className="bg-white p-3 md:p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col justify-between gap-2 md:gap-3">
                <div>
                    <div className="flex justify-between items-start">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Atom className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                        </div>
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="p-1 md:p-2 rounded-full hover:bg-gray-100"
                        >
                            <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4 text-gray-400 group-hover:text-[#0e4d38]" />
                        </motion.div>
                    </div>
                    <h3 className="text-sm md:text-lg font-semibold mt-2 md:mt-3 text-gray-800 group-hover:text-[#0e4d38] transition-colors line-clamp-2">
                        {course.name}
                    </h3>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{course.name.includes('M.Sc') ? '2 Years' : '3/4 Years'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Bookmark className="w-3 h-3" />
                        <span>{course.name.includes('M.Sc') ? 'PG' : 'UG'}</span>
                    </div>
                </div>
            </div>
        </Link>
    </motion.div>
);

const Department = ({ title, icon, courses }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-4 md:p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 border-b pb-4">
            <div className="p-2 md:p-3 bg-[#0e4d38] rounded-lg text-white">
                {icon}
            </div>
            <h4 className="text-lg md:text-xl font-semibold text-[#0e4d38]">{title}</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {courses.map((course, index) => (
                <CourseCard key={index} course={course} index={index} />
            ))}
        </div>

    </motion.div>
);

const FeatureList = ({ title, items, icon }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-4 md:p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 border-b pb-4">
            <div className="p-2 md:p-3 bg-[#0e4d38] rounded-xl text-white">
                {icon}
            </div>
            <h4 className="text-lg md:text-xl font-semibold text-[#0e4d38]">{title}</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {items.map((item, index) => (
                <motion.div
                    key={index}
                    className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                    whileHover={{ scale: 1.02 }}
                >
                    <div className="flex items-start gap-2">
                        <div className="w-2 h-2 mt-1.5 bg-[#23376c] rounded-full flex-shrink-0" />
                        <span className="text-gray-700 text-xs md:text-sm">{item}</span>
                    </div>
                </motion.div>
            ))}
        </div>

    </motion.div>
);

const HeroSection = () => (
    <div className="relative overflow-hidden bg-[#0e4d38] text-white py-8 md:py-16 px-4 md:px-6 rounded-xl mb-6 md:mb-8 mt-16 md:mt-20">
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl"
        >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                School of Applied Sciences
            </h1>
            <p className="text-sm md:text-lg text-gray-200 mb-4 md:mb-6 max-w-3xl">
                Empowering future scientists with comprehensive education and research opportunities.
                Join us in exploring the frontiers of science and innovation.
            </p>
            <Link to="https://admission.maya.edu.in/">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-[#0e4d38] px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold flex items-center gap-2 group"
                >
                    Apply Now
                    <Rocket className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>
            </Link>
        </motion.div>
    </div>
);

const Science = () => {
    return (
        <div className="min-h-screen bg-gray-50 font-poppins">
            <Title canonicalPath="/programmes/ug-courses/sciences" />
            <Title title="School of Applied Sciences at Maya Devi University" description="Explore the School of Applied Sciences at MDU, Dehradun. Gain practical knowledge and skills for a successful career in science and technology." />
            <div className="max-w-7xl mx-auto pt-16 md:pt-20 pb-8 md:pb-16 px-4 space-y-6 md:space-y-8">
                <HeroSection />
                <Stats />

                <div className="space-y-6 md:space-y-8">
                    <Department
                        title="Bachelor of Science Programs"
                        icon={<GraduationCap className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={bscCourses}
                    />
                    {/* <Department
                        title="Master of Science Programs"
                        icon={<Microscope className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={mscCourses}
                    /> */}
                    <p className="text-red-600 font-semibold text-center">
                        *For BSc Hons, duration of MSc will be 1 year*
                    </p>
                </div>


                <div className="space-y-6 md:space-y-8">
                    <FeatureList
                        title="USPs"
                        icon={<Stars className="w-6 h-6 text-[#0e4d38]" />}
                        items={[
                            "State-of-the-Art Research Facilities",
                            "Experienced Faculty with Research Background",
                            "Industry-Academia Collaborations",
                            "Choice Based Credit System (CBCS)",
                            "Research-Oriented Curriculum",
                            "Modern Laboratory Infrastructure",
                            "International Research Exposure",
                            "Project-Based Learning",
                            "Scientific Paper Publications",
                            "Research Funding Opportunities",
                            "Interdisciplinary Approach",
                            "Industry-Relevant Training"
                        ]}
                    />

                    <FeatureList
                        title="Career Options"
                        icon={<FlaskConical className="w-6 h-6 text-[#0e4d38]" />}
                        items={[
                            "Research Scientist",
                            "Data Analyst",
                            "Environmental Consultant",
                            "Laboratory Manager",
                            "Quality Control Specialist",
                            "Science Educator",
                            "R&D Professional",
                            "Technical Writer",
                            "Patent Analyst",
                            "Environmental Officer",
                            "Scientific Researcher",
                            "Product Development Scientist"
                        ]}
                    />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Science;